import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { OnboardingUser } from '../services/HxcStagingService';
import { logger } from 'utils/logger';

const log = logger.getLogger('ProcessOnboardingDialog');

interface ProcessOnboardingDialogProps {
  open: boolean;
  onClose: () => void;
  users: OnboardingUser[];
  onProcess: () => Promise<{ emailCount: number }>;
}

type DialogState = 'confirmation' | 'processing' | 'success' | 'error';

const ProcessOnboardingDialog: React.FC<ProcessOnboardingDialogProps> = ({
  open,
  users,
  onClose,
  onProcess
}) => {
  const [dialogState, setDialogState] = useState<DialogState>('confirmation');
  const [emailCount, setEmailCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [autoCloseTimer, setAutoCloseTimer] = useState<number | null>(null);

  // Calculate number of emails that will be sent (users with valid onboard dates)
  const validEmailCount = users.filter(user => 
    user.onboardDate === null || (user.onboardDate && (new Date(user.onboardDate) <= new Date()))
  ).length;

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setDialogState('confirmation');
      setEmailCount(0);
      setError(null);
      setAutoCloseTimer(null);
    }
  }, [open]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Handle auto-close after success
  useEffect(() => {
    if (dialogState === 'success') {
      const timer = setTimeout(() => {
        handleClose();
      }, 5000); // 5 seconds
      setAutoCloseTimer(5);
      
      const countdown = setInterval(() => {
        setAutoCloseTimer(prev => {
          if (prev === null || prev <= 1) {
            clearInterval(countdown);
            return null;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        clearTimeout(timer);
        clearInterval(countdown);
      };
    }
  }, [dialogState, handleClose]);

  const handleCancel = () => {
    if (dialogState !== 'processing') {
      onClose();
    }
  };

  const handleProcess = async () => {
    try {
      setDialogState('processing');
      setError(null);
      
      log.log(`Processing onboarding for ${validEmailCount} users`);
      
      const result = await onProcess();
      
      setEmailCount(result.emailCount);
      setDialogState('success');
      
      log.log(`Successfully processed ${result.emailCount} onboarding emails`);
    } catch (err: any) {
      log.error('Error processing onboarding:', err);
      setError(err.message || 'Failed to send emails. Please try again.');
      setDialogState('error');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
    >
      {dialogState === 'confirmation' && (
        <>
          <DialogTitle>Send Onboarding Emails</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              This will send onboarding emails to <strong>{validEmailCount}</strong> user{validEmailCount !== 1 ? 's' : ''} with valid onboard dates.
            </Typography>
            
            {validEmailCount === 0 && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
                ⚠️ No users with valid onboard dates to process.
              </Typography>
            )}
            
            {validEmailCount > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Only users with onboard dates in the past or present will receive emails.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button 
              onClick={handleProcess} 
              variant="contained" 
              color="primary"
              disabled={validEmailCount === 0}
            >
              Send Emails
            </Button>
          </DialogActions>
        </>
      )}

      {dialogState === 'processing' && (
        <>
          <DialogTitle>Sending Emails</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="body1" sx={{ mt: 3 }}>
                Sending emails to {validEmailCount} user{validEmailCount !== 1 ? 's' : ''}...
              </Typography>
            </Box>
          </DialogContent>
        </>
      )}

      {dialogState === 'success' && (
        <>
          <DialogTitle>Emails Sent Successfully</DialogTitle>
          <DialogContent>
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" color="success.main" gutterBottom>
                ✓ Successfully sent {emailCount} email{emailCount !== 1 ? 's' : ''}!
              </Typography>
              {autoCloseTimer !== null && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  This dialog will close automatically in {autoCloseTimer} second{autoCloseTimer !== 1 ? 's' : ''}...
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClose} variant="contained">
              Close
            </Button>
          </DialogActions>
        </>
      )}

      {dialogState === 'error' && (
        <>
          <DialogTitle>Error Sending Emails</DialogTitle>
          <DialogContent>
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" color="error.main" gutterBottom>
                ❌ Failed to send emails
              </Typography>
              {error && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {error}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClose} variant="contained">
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ProcessOnboardingDialog;